import { useState } from 'react';
import { migrateAllProjects, migrateProjects, backupData } from '../utils/migration';

function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T | ((val: T) => T)) => void] {
  // Get from local storage then parse stored json or return initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        
        // Apply migration for project data
        if (key === 'rapid-project-data') {
          const migrated = migrateAllProjects(parsed);
          // Save migrated data back to localStorage
          window.localStorage.setItem(key, JSON.stringify(migrated));
          return migrated as T;
        }

        // Apply migration for projects list
        if (key === 'rapid-projects') {
          const migrated = migrateProjects(parsed);
          // Save migrated data back to localStorage
          window.localStorage.setItem(key, JSON.stringify(migrated));
          return migrated as T;
        }

        return parsed;
      }
      return initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Create backup for project data before updating
      if (key === 'rapid-project-data' && storedValue) {
        backupData(key, storedValue);
      }
      
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}

export default useLocalStorage;